import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { cn } from '@cotera/client/v0/lib/utils';
import { VIEW_UNAVAILABLE_HINT } from './share-targets';

export type SharePermission = 'view' | 'edit';

/** A stored level as one the dialog offers: anything above View is Edit here. */
export const toSharePermission = (
  level: string | null | undefined
): SharePermission => (level === 'view' ? 'view' : 'edit');

export const permissionLabel = (permission: SharePermission): string =>
  permission === 'edit' ? 'Can edit' : 'Can view';

/**
 * "Can edit / Can view", plus an optional destructive "Remove" at the bottom —
 * the one control a person's row needs, as in Docs.
 *
 * View is shown but disabled where the resource has no read-only page yet, with
 * the reason in its label, so the option isn't a mystery by its absence.
 */
export const SharePermissionSelect: React.FC<{
  value: SharePermission;
  onChange: (next: SharePermission) => void;
  allowView: boolean;
  /** Adds a "Remove access" (or custom label) entry below a separator. */
  onRemove?: () => void;
  removeLabel?: string;
  disabled?: boolean;
  'aria-label': string;
  className?: string;
}> = ({
  value,
  onChange,
  allowView,
  onRemove,
  removeLabel = 'Remove access',
  disabled = false,
  'aria-label': ariaLabel,
  className,
}) => (
  <Select
    value={value}
    disabled={disabled}
    onValueChange={(next) => {
      if (next === 'remove') {
        onRemove?.();
        return;
      }
      if (next === 'view' || next === 'edit') {
        onChange(next);
      }
    }}
  >
    <SelectTrigger
      size="sm"
      aria-label={ariaLabel}
      className={cn(
        'h-7 shrink-0 gap-1 border-none px-2 text-xs shadow-none hover:bg-muted',
        className
      )}
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent align="end" className="max-w-72">
      <SelectItem value="edit">Can edit</SelectItem>
      {/* A legacy View grant on a type without a read-only page still shows as
          itself; the hint would otherwise echo into the trigger. */}
      <SelectItem value="view" disabled={!allowView && value !== 'view'}>
        <span className="flex flex-col">
          <span>Can view</span>
          {allowView || value === 'view' ? null : (
            <span className="text-xs text-muted-foreground whitespace-normal">
              {VIEW_UNAVAILABLE_HINT}
            </span>
          )}
        </span>
      </SelectItem>
      {onRemove === undefined ? null : (
        <>
          <SelectSeparator />
          <SelectItem value="remove" className="text-destructive">
            {removeLabel}
          </SelectItem>
        </>
      )}
    </SelectContent>
  </Select>
);
